import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, Eye, Ban, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { http, apiError, formatDateTime } from "../../lib/api";

const shareState = (s) => {
  if (s.status === "failed") return ["Failed to send", "text-rose-300"];
  if (s.revoked_at) return [s.revoke_reason || "Revoked", "text-slate-400"];
  if (new Date(s.expires_at) <= new Date()) return ["Expired", "text-slate-400"];
  return ["Active link", "text-emerald-300"];
};

const ShareHistory = ({ shares, onRevoke }) => (
  <ul className="mt-4 divide-y divide-white/10 border-y hairline" data-testid="share-history">
    {shares.map((s) => {
      const [label, cls] = shareState(s);
      const active = label === "Active link";
      return (
        <li key={s.id} className="py-3 text-xs text-slate-300 flex flex-wrap items-start gap-x-4 gap-y-1" data-testid={`share-row-${s.id}`}>
          <div className="flex-1 min-w-[200px]">
            <p className="text-sm text-slate-100 break-all">{s.recipient}</p>
            <p>{formatDateTime(s.sent_at || s.created_at)} · by {s.sent_by}</p>
            <p><span className={cls}>{label}</span>{s.status === "sent" ? ` · expires ${formatDateTime(s.expires_at)} · opened ${s.download_count || 0}×` : ""}</p>
          </div>
          {active && <button className="btn-ghost !py-1.5 !px-3 !text-xs" onClick={() => onRevoke(s)} data-testid={`share-revoke-${s.id}`}><Ban className="w-3 h-3" /> Revoke</button>}
        </li>
      );
    })}
  </ul>
);

const Composer = ({ item, info, onSent }) => {
  const [subject, setSubject] = useState(info.subject);
  const [message, setMessage] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [step, setStep] = useState("edit");
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState("");
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const loadPreview = async () => {
    setBusy("preview");
    try {
      const { data } = await http.get(`/admin/inquiries/${item.id}/share/preview`, { responseType: "blob" });
      setPreview(URL.createObjectURL(new Blob([data], { type: "application/pdf" })));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };
  const send = async () => {
    setBusy("send");
    try {
      await http.post(`/admin/inquiries/${item.id}/share`, { confirm_email: info.recipient, subject, message, confirmed });
      toast.success(`Shareable PDF emailed to ${info.recipient}`);
      onSent();
    } catch (e) {
      toast.error(apiError(e));
      setStep("edit");
    } finally {
      setBusy("");
    }
  };
  return (
    <div className="space-y-4">
      <div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-ghost !py-2 !px-3 !text-xs" onClick={loadPreview} disabled={!!busy} data-testid="share-preview-button">{busy === "preview" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />} {preview ? "Refresh preview" : "Preview PDF"}</button>
          {preview && <a href={preview} target="_blank" rel="noreferrer" className="btn-ghost !py-2 !px-3 !text-xs" data-testid="share-preview-open"><ExternalLink className="w-3 h-3" /> Open full size</a>}
        </div>
        {preview && <iframe title="Shareable PDF preview" src={preview} className="w-full h-72 mt-3 bg-white border hairline hidden sm:block" data-testid="share-preview-frame" />}
      </div>
      <div>
        <label className="label">Recipient (customer email on this inquiry)</label>
        <p className="field !py-2.5 break-all select-all" data-testid="share-recipient">{info.recipient}</p>
        <label className="flex items-start gap-3 mt-2 cursor-pointer text-sm text-slate-200">
          <input type="checkbox" className="w-4 h-4 mt-0.5 accent-[#d4af37]" checked={confirmed} onChange={(e) => { setConfirmed(e.target.checked); setStep("edit"); }} data-testid="share-confirm-email" />
          I confirm this is the customer's correct email address.
        </label>
      </div>
      <div><label className="label">Subject</label><input className="field !py-2.5" maxLength={150} value={subject} onChange={(e) => { setSubject(e.target.value); setStep("edit"); }} data-testid="share-subject-input" /></div>
      <div><label className="label">Brief message (optional)</label><textarea rows={3} maxLength={1500} className="field" placeholder="e.g. Thanks for your time on the phone today. Here's a summary of what we discussed." value={message} onChange={(e) => { setMessage(e.target.value); setStep("edit"); }} data-testid="share-message-input" /></div>
      <p className="text-xs text-slate-400">The email contains a secure download link that works for {info.link_days} days. Sending a new one turns off older links. Internal notes, staff comments and assignment details are never included.</p>
      {step === "edit" ? (
        <button className="btn-gold w-full" disabled={!confirmed || subject.trim().length < 3} onClick={() => setStep("confirm")} data-testid="share-review-button"><Send className="w-4 h-4" /> Review & send</button>
      ) : (
        <div className="border border-[var(--gold)] p-4 space-y-3" data-testid="share-final-confirm">
          <p className="text-sm text-slate-100">Send the Shareable PDF for {item.ref} to <span className="text-gold break-all">{info.recipient}</span>?</p>
          <div className="flex gap-2">
            <button className="btn-gold flex-1" onClick={send} disabled={busy === "send"} data-testid="share-send-button">{busy === "send" && <Loader2 className="w-4 h-4 animate-spin" />} Confirm & send</button>
            <button className="btn-ghost" onClick={() => setStep("edit")} disabled={busy === "send"} data-testid="share-cancel-button">Back</button>
          </div>
        </div>
      )}
    </div>
  );
};

export const SharePdf = ({ item }) => {
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => http.get(`/admin/inquiries/${item.id}/share`).then(({ data }) => setInfo(data)).catch(() => setInfo(null));
  useEffect(() => { load(); }, [item.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const revoke = async (s) => {
    try {
      await http.post(`/admin/inquiries/${item.id}/share/${s.id}/revoke`);
      toast.success("Link revoked. It no longer works.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  if (!info) return null;
  const shares = Array.isArray(info.shares) ? info.shares : [];
  return (
    <section data-testid="share-pdf">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="eyebrow">Email Shareable PDF</p>
        <button className="btn-gold !py-2.5 !px-4" onClick={() => setOpen(true)} disabled={!info.recipient} data-testid="share-open-button"><Send className="w-4 h-4" /> {shares.some((s) => s.status === "sent") ? "Send new link" : "Email to customer"}</button>
      </div>
      {shares.length ? <ShareHistory shares={shares} onRevoke={revoke} /> : <p className="text-sm text-slate-400 mt-3">Not sent yet.</p>}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto bg-[var(--ink-2)] border-white/15 text-slate-100" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Email Shareable PDF</DialogTitle>
            <DialogDescription className="text-slate-400">{item.ref} · {item.name}</DialogDescription>
          </DialogHeader>
          {open && <Composer item={item} info={info} onSent={() => { setOpen(false); load(); }} />}
        </DialogContent>
      </Dialog>
    </section>
  );
};
